import React from 'react'
import { Row, Col } from 'react-grid-system'

import Layout from '../Layout'
import PageHeading from '../PageHeading'

import styles from './PrivacyPolicy.module.scss'

const PrivacyPolicy = () => {
  return (
    <Layout>
      <Row>
        <Col>
          <PageHeading>
            <h1>Privacy Policy</h1>
          </PageHeading>

          <div className={styles.policy}>
            <div className={styles.header}>
              <p>
                <b>Buygoon Inc.</b>
              </p>
              <p>Effective Date: May 23, 2026 &bull; www.buygoon.com</p>
              <p>
                <em>Governing Law: Province of Ontario, Canada</em>
              </p>
            </div>

          <p>
            <b>GEOGRAPHIC RESTRICTION NOTICE:</b> Buygoon.com is available exclusively to residents
            of the United States and Canada, excluding the Province of Quebec. If you are located in
            the European Union, European Economic Area, United Kingdom, Switzerland, or the Province
            of Quebec, Canada, this service is not available to you and you will be redirected
            accordingly. By accessing this website, you represent that you are located in a
            permitted jurisdiction.
          </p>

          <h2>1. Introduction</h2>

          <p>
            Buygoon Inc. (&ldquo;Buygoon,&rdquo; &ldquo;we,&rdquo; &ldquo;us,&rdquo; or
            &ldquo;our&rdquo;) is a cashback rewards and referral platform operating at
            www.buygoon.com. We are dedicated to the responsible management of personal information
            collected through our website and any related interactions. This Privacy Policy explains
            what information we collect, how we use it, with whom we share it, and what rights you
            have in relation to it.
          </p>

          <p>Our primary goals in processing personal information include:</p>

          <ul>
            <li>
              (a)&nbsp;&nbsp;Enabling the cashback rewards and referral network services that are
              the core of the Buygoon platform.
            </li>
            <li>
              (b)&nbsp;&nbsp;Enhancing the user experience on our platform by understanding member
              needs and preferences.
            </li>
            <li>
              (c)&nbsp;&nbsp;Providing timely support and responding to inquiries or service
              requests.
            </li>
            <li>
              (d)&nbsp;&nbsp;Improving our products and services to meet the evolving demands of our
              members.
            </li>
            <li>
              (e)&nbsp;&nbsp;Conducting necessary business operations, such as account management
              and Cashback crediting.
            </li>
            <li>
              (f)&nbsp;&nbsp;Complying with applicable laws and regulations, including tax reporting
              obligations.
            </li>
          </ul>

          <p>
            <b>Accountability:</b> Buygoon Inc. is accountable for all personal information under
            its control. Privacy@buygoon.com is the designated accountable contact responsible for
            Buygoon&rsquo;s compliance with applicable privacy laws including PIPEDA. Privacy
            inquiries, complaints, and requests for information about Buygoon&rsquo;s privacy
            practices may be directed to{' '}
            <a href="mailto:privacy@buygoon.com">privacy@buygoon.com</a>. We will respond to all
            legitimate privacy inquiries in a timely manner.
          </p>

          <h2>2. Scope and Application</h2>

          <p>
            This Privacy Policy applies to registered Members of the Buygoon platform and to
            individuals who interact with the cookie wall consent mechanism at www.buygoon.com. It
            governs the collection, use, storage, sharing, and protection of personal information in
            connection with the Buygoon platform.
          </p>

          <p>
            This Privacy Policy is governed by and construed in accordance with the laws of the
            Province of Ontario and the applicable federal laws of Canada, including the Personal
            Information Protection and Electronic Documents Act (PIPEDA). Any disputes arising in
            connection with this Privacy Policy shall be resolved in accordance with the dispute
            resolution provisions set out in Buygoon&rsquo;s Terms and Conditions, which include
            mandatory binding arbitration in Ontario, Canada and a waiver of the right to
            participate in class action proceedings. Please review the Terms and Conditions for
            full details on dispute resolution.
          </p>

          <h2>3. About Buygoon &mdash; How the Platform Works</h2>

          <p>
            Understanding how Buygoon operates is essential to understanding why we collect and use
            certain data. This section explains the platform&rsquo;s core mechanics.
          </p>

          <h4>3.1 Cashback Mechanism</h4>

          <p>
            Buygoon is a cashback rewards platform. When you click through to a participating
            merchant retailer via Buygoon, we place a tracking cookie on your browser at the moment
            of click-through. This cookie records that your shopping trip originated from Buygoon,
            which allows the merchant&rsquo;s affiliate network to verify your purchase and credit
            Buygoon with an affiliate commission. A portion of that commission is then credited to
            your Buygoon account as Cashback.
          </p>

          <p>
            This tracking cookie is not optional &mdash; it is the fundamental technical mechanism
            by which the Cashback service operates. Without it, we cannot verify purchases or credit
            Cashback to your account. By consenting to use our platform, you consent to this
            tracking as a necessary component of the service you are receiving.
          </p>

          <p>
            Simultaneously, at the same moment of click-through, Buygoon&rsquo;s system
            automatically appends your unique Buygoon member identifier &mdash; a pseudonymous
            numerical sub-ID &mdash; to the affiliate link. This sub-ID travels with you to the
            merchant retailer&rsquo;s website and is reported back to Buygoon by the applicable
            affiliate network partner to confirm which member&rsquo;s account should receive
            Cashback credit. The sub-ID is a pseudonymous technical identifier only &mdash; it
            cannot be used by the merchant retailer or any affiliate network partner to identify you
            as an individual. Together, the tracking cookie and the sub-ID constitute the dual
            attribution mechanism described in Buygoon&rsquo;s Terms and Conditions, both of which
            are non-optional and non-severable components of the Cashback service.
          </p>

          <h4>3.2 Referral Network and Buygoon Bonus Cash</h4>

          <p>
            In addition to direct Cashback, Buygoon operates a referral rewards system called
            Buygoon Bonus Cash. When you refer new members to Buygoon and those members shop through
            the platform, you earn Buygoon Bonus Cash based on their shopping activity. This
            referral relationship is tracked and rewarded across up to four degrees of connection
            &mdash; meaning you can earn Buygoon Bonus Cash not only from members you directly
            referred, but also from members those people referred, and so on, for up to four levels.
          </p>

          <p>
            To operate this system, we track referral relationships internally. We record which
            member referred which other member, and we calculate and credit Buygoon Bonus Cash
            rewards based on network shopping activity. This data is used solely for the purpose of
            calculating and crediting rewards and is never shared between members beyond the limited
            visibility described in Section 3.3.
          </p>

          <h4>3.3 Referral Network Visibility and Pseudonym Policy</h4>

          <p>Buygoon is deliberately designed to protect member privacy within the referral network:</p>

          <ul>
            <li>
              (a)&nbsp;&nbsp;Your display name is visible to the member who directly referred you.
            </li>
            <li>
              (b)&nbsp;&nbsp;You can see the display names of members you have directly referred.
            </li>
            <li>
              (c)&nbsp;&nbsp;No other personal information is ever shared between members &mdash;
              including email addresses, Cashback amounts, purchase history, transaction details, or
              merchant activity.
            </li>
            <li>
              (d)&nbsp;&nbsp;You may use any display name or pseudonym you choose. Real names are
              not required at any point.
            </li>
          </ul>

          <h4>3.4 PayPal Account Connection</h4>

          <p>
            To receive Cashback payments, members connect their PayPal account to Buygoon. This
            connection is handled entirely through PayPal&rsquo;s own secure OAuth authentication
            flow. Buygoon does not receive, store, or have access to your PayPal credentials, bank
            account details, card numbers, or any other financial account information. We receive
            only a secure authorization token confirming that the PayPal connection has been
            established. All financial data remains with PayPal and is governed by PayPal&rsquo;s
            own privacy policy, available on PayPal&rsquo;s website.
          </p>

          <h2>4. Cookie Wall and Consent Model</h2>

          <p>
            Buygoon.com operates a full cookie wall. This means that no page content, scripts,
            pixels, tracking technologies, or data collection of any kind occurs until you provide
            explicit affirmative consent by clicking &lsquo;Accept All &amp; Enter Buygoon.&rsquo;
            Nothing loads until you say yes.
          </p>

          <p>
            Consent on Buygoon is binary and all-inclusive &mdash; you either consent to all cookies
            and tracking technologies as a single integrated package, or you do not access the
            service. This is because the tracking technologies that enable Cashback attribution,
            referral network tracking, analytics, and merchant advertising are collectively
            fundamental to the service and cannot be selectively disabled without disabling the
            service entirely.
          </p>

          <p>
            If you decline consent, you will be redirected to a page confirming that the service is
            not accessible without consent.
          </p>

          <p>
            Your consent is recorded, timestamped, and securely stored by Cookiebot (Usercentrics
            A/S). You may withdraw your consent at any time via the Privacy Settings icon on the
            site. Please note that withdrawing consent will immediately disable your access to the
            Buygoon platform and your ability to earn future Cashback and Buygoon Bonus Cash.
            Cashback and Buygoon Bonus Cash amounts already credited to your account remain
            accessible upon re-consent and login.
          </p>

          <p>
            <b>Cookie Wall Consent and Browser Privacy Signals:</b> A member&rsquo;s affirmative
            acceptance of the cookie wall constitutes explicit prior informed consent that governs
            over any browser-level or device-level privacy preference signal, including signals sent
            via the Global Privacy Control or similar mechanisms. Members whose browser or device is
            configured to send such signals and who do not wish to consent to Buygoon&rsquo;s
            tracking technologies should not accept the cookie wall and should not access the
            platform.
          </p>

          <h2>5. Information We Collect</h2>

          <h4>5.1 Information You Provide Directly</h4>

          <ul>
            <li>
              (a)&nbsp;&nbsp;<b>Email address</b> &mdash; collected at account registration for
              account management, transactional notifications, and communication.
            </li>
            <li>
              (b)&nbsp;&nbsp;<b>Display name or pseudonym</b> &mdash; chosen by you at registration.
              Real names are not required.
            </li>
            <li>
              (c)&nbsp;&nbsp;<b>Address</b> &mdash; collected for account purposes where applicable.
            </li>
            <li>
              (d)&nbsp;&nbsp;<b>Legal name, Social Security Number or Tax Identification Number (TIN)</b>{' '}
              &mdash; collected when required for tax reporting purposes, as described in Section
              10. This information is collected only when your Cashback and Buygoon Bonus Cash
              earnings reach applicable reporting thresholds under US or Canadian tax law.
            </li>
            <li>
              (e)&nbsp;&nbsp;<b>PayPal authorization token</b> &mdash; a secure technical token
              confirming PayPal account connection. No financial credentials are collected or stored
              by Buygoon.
            </li>
          </ul>

          <h4>5.2 Information Collected Automatically</h4>

          <ul>
            <li>
              (a)&nbsp;&nbsp;<b>IP address</b> &mdash; collected by our hosting infrastructure,
              monitoring services, and geolocation provider.
            </li>
            <li>
              (b)&nbsp;&nbsp;<b>Device ID</b> &mdash; collected by analytics and tracking
              technologies.
            </li>
            <li>
              (c)&nbsp;&nbsp;<b>Browser information and language</b> &mdash; collected by
              performance monitoring and analytics services.
            </li>
            <li>
              (d)&nbsp;&nbsp;<b>IP-based approximate location</b> &mdash; collected by Abstract API
              for geographic access control purposes.
            </li>
            <li>
              (e)&nbsp;&nbsp;<b>Browsing history on Buygoon</b> &mdash; pages visited, merchants
              viewed, and click-through activity within the platform.
            </li>
            <li>
              (f)&nbsp;&nbsp;<b>Purchase history</b> &mdash; transaction data necessary for Cashback
              verification and crediting.
            </li>
            <li>
              (g)&nbsp;&nbsp;<b>Interaction logs</b> &mdash; clicks, time spent on pages, and
              platform usage patterns collected by monitoring and analytics services.
            </li>
          </ul>

          <h4>5.3 Information from Third Parties</h4>

          <ul>
            <li>
              (a)&nbsp;&nbsp;<b>Affiliate attribution data</b> &mdash; affiliate network partners,
              including Commission Junction and Impact among others, provide purchase verification
              data confirming completed transactions with merchant retailers.
            </li>
            <li>
              (b)&nbsp;&nbsp;<b>Consent records</b> &mdash; Cookiebot records and provides us with
              your consent decision, timestamp, and consent ID.
            </li>
          </ul>

          <h2>6. How We Use Your Information</h2>

          <p>We use the personal information we collect for the following purposes:</p>

          <ul>
            <li>
              (a)&nbsp;&nbsp;<b>Authentication and security</b> &mdash; verifying your identity,
              managing your account, and protecting the platform from fraud and unauthorized access.
            </li>
            <li>
              (b)&nbsp;&nbsp;<b>Cashback attribution and crediting</b> &mdash; tracking
              click-throughs to merchant sites, verifying purchases, and crediting Cashback to your
              account.
            </li>
            <li>
              (c)&nbsp;&nbsp;<b>Referral network management</b> &mdash; tracking referral
              relationships and calculating and crediting Buygoon Bonus Cash rewards.
            </li>
            <li>
              (d)&nbsp;&nbsp;<b>Customizing and adapting user experience</b> &mdash; remembering
              your preferred stores, saved merchants, and account preferences.
            </li>
            <li>
              (e)&nbsp;&nbsp;<b>Analytics and performance tracking</b> &mdash; understanding how the
              platform is used and improving its performance and functionality.
            </li>
            <li>
              (f)&nbsp;&nbsp;<b>Communication</b> &mdash; sending transactional emails related to
              your account, Cashback credits, and platform updates.
            </li>
            <li>
              (g)&nbsp;&nbsp;<b>Direct marketing</b> &mdash; sending promotional communications
              about offers, merchants, and platform features, subject to your consent.
            </li>
            <li>
              (h)&nbsp;&nbsp;<b>Fraud prevention and risk management</b> &mdash; monitoring for
              unusual or unauthorized activity.
            </li>
            <li>
              (i)&nbsp;&nbsp;<b>Geographic access control</b> &mdash; verifying that visitors are
              located in a permitted jurisdiction before granting access.
            </li>
            <li>
              (j)&nbsp;&nbsp;<b>Tax reporting and compliance</b> &mdash; collecting, retaining, and
              reporting financial information as required by applicable tax laws, including IRS and
              CRA obligations described in Section 10.
            </li>
            <li>
              (k)&nbsp;&nbsp;<b>Compliance with legal obligations</b> &mdash; maintaining consent
              records and complying with applicable privacy, tax, and regulatory laws.
            </li>
            <li>
              (l)&nbsp;&nbsp;<b>Automated processing</b> &mdash; to the extent required by
              applicable law, Buygoon will disclose the use of automated or AI-assisted processes
              in connection with its services where such processes may materially affect a
              member&rsquo;s account, Cashback eligibility, or Buygoon Bonus Cash.
            </li>
          </ul>

          <h2>7. Data Sharing and Third-Party Service Providers</h2>

          <p>
            We may share your personal information with third-party service providers who perform
            services on our behalf. Such providers have access to personal information on a
            need-to-know basis only and are required to maintain comparable protection for your
            personal information while it is in their custody or control, consistent with the
            requirements of applicable Canadian privacy law.
          </p>

          <p>
            We do not sell your personal information to third parties. We do not share your personal
            information with third parties for their own independent marketing purposes.
          </p>

          <p>
            The following table provides a complete list of third-party services that may receive
            data about you or your activity on Buygoon.com. Where a Tax Compliance Service Provider
            has not yet been specifically engaged, that row will be updated with the provider&rsquo;s
            details upon engagement:
          </p>

          <table>
            <thead>
              <tr>
                <th>Service Name</th>
                <th>Provider</th>
                <th>Purpose(s)</th>
                <th>Data Collected</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>New Relic Browser Monitoring</td>
                <td>New Relic, Inc.</td>
                <td>Analytics and performance tracking; Fraud prevention and risk management</td>
                <td>IP address; Browser information and language; Interaction logs</td>
              </tr>
              <tr>
                <td>Sentry Error Monitoring</td>
                <td>Functional Software, Inc. (Sentry)</td>
                <td>Fraud prevention and risk management; Authentication and security</td>
                <td>IP address; Browser information and language; Interaction logs</td>
              </tr>
              <tr>
                <td>Postmark Transactional Email</td>
                <td>Wildbit LLC (Postmark)</td>
                <td>Communication efforts; Customer support</td>
                <td>Email address; Email content</td>
              </tr>
              <tr>
                <td>Heroku Cloud Hosting</td>
                <td>Salesforce, Inc. (Heroku)</td>
                <td>Application hosting; Authentication and security; Processing transactions</td>
                <td>IP address; Request logs; Environment routing data</td>
              </tr>
              <tr>
                <td>Amazon Web Services (AWS S3)</td>
                <td>Amazon Web Services EMEA SARL</td>
                <td>File storage; Static asset hosting</td>
                <td>IP address; Browser information (on static asset requests)</td>
              </tr>
              <tr>
                <td>Redis Cloud</td>
                <td>Redis Inc.</td>
                <td>Background job processing; Session management via WebSockets</td>
                <td>User IDs; Transaction data; Retailer IDs (server-side only)</td>
              </tr>
              <tr>
                <td>Memcached Cloud</td>
                <td>Redis Inc. / ObjectRocket</td>
                <td>Application caching and performance</td>
                <td>Application cache fragments (server-side only)</td>
              </tr>
              <tr>
                <td>Commission Junction (CJ Affiliate)</td>
                <td>Publicis Groupe (CJ Affiliate)</td>
                <td>Affiliate Cashback attribution; Marketing and advertising</td>
                <td>
                  IP address; Browser information and language; Browsing history; Purchase history
                </td>
              </tr>
              <tr>
                <td>Impact Affiliate Network</td>
                <td>Impact.com</td>
                <td>Affiliate Cashback attribution; Marketing and advertising</td>
                <td>
                  IP address; Browser information and language; Browsing history; Purchase history
                </td>
              </tr>
              <tr>
                <td>Google Analytics</td>
                <td>Google Ireland Limited</td>
                <td>Analytics and performance tracking</td>
                <td>IP address; Device ID; Browsing history; Interaction logs</td>
              </tr>
              <tr>
                <td>Cookiebot Consent Management</td>
                <td>Usercentrics A/S</td>
                <td>Consent management; Compliance with legal obligations</td>
                <td>IP address; Consent timestamp; Consent state</td>
              </tr>
              <tr>
                <td>Abstract API Geolocation</td>
                <td>Abstract API</td>
                <td>IP-based geolocation for geographic access control</td>
                <td>IP address; IP-based approximate location</td>
              </tr>
              <tr>
                <td>Tax Compliance Service Provider (TBD)</td>
                <td>To be disclosed upon engagement</td>
                <td>Tax identification number collection; IRS Form 1099 filing; CRA tax reporting</td>
                <td>
                  Legal name; Social Security Number or Tax Identification Number; Payment amounts;
                  Mailing address
                </td>
              </tr>
            </tbody>
          </table>

          <p>
            <b>Affiliate Network Partners &mdash; Extended Data Use Notice:</b> Commission Junction,
            Impact, and other affiliate network partners through whom Cashback attribution is
            facilitated may use attribution data for their own purposes beyond transaction
            verification, including contributing it to their own user profiles, combining it with
            data collected from other sources, and making it available to their other business
            clients. This data use by affiliate network partners is governed by their own privacy
            policies, available on their respective websites. By consenting to use the Buygoon
            platform via the cookie wall, you consent to this data collection and use as a necessary
            and non-severable component of the Cashback service.
          </p>

          <p>
            <b>Business Transfers:</b> Personal information may be disclosed to third parties
            &mdash; including potential buyers, sellers, lenders, investors, and their respective
            advisors and representatives &mdash; in connection with the actual or contemplated
            merger, acquisition, sale of assets, reorganization, financing, due diligence,
            liquidation, dissolution, or other disposal of all or part of Buygoon&rsquo;s business
            or assets. Members acknowledge that such disclosure is a legitimate business purpose and
            consent to it as a condition of using the service.
          </p>

          <p>
            <b>Data Processing Agreements:</b> Where required by applicable law, we enter into Data
            Processing Agreements (DPAs) with our third-party service providers. These agreements
            require providers to implement appropriate technical and organizational measures to
            protect your personal information and to process it only in accordance with our
            instructions and applicable law.
          </p>

          <p>
            <b>Third-Party Merchant Sites:</b> When you click through to a merchant retailer via a
            Buygoon affiliate link, you are leaving the Buygoon platform and entering a website that
            is owned and operated by a third party. Buygoon has no control over, and accepts no
            responsibility for, the content, privacy practices, data collection, or security
            measures of any merchant retailer or other third-party website to which you are
            directed. The privacy policy of Buygoon does not apply to any third-party site you visit
            through the Buygoon platform. We strongly encourage you to review the privacy policy of
            every merchant retailer or other external site you visit before providing any personal
            information to that site.
          </p>

          <h2>8. Cookies and Tracking Technologies</h2>

          <p>
            Buygoon.com uses cookies and tracking technologies that are fundamental to how our
            cashback rewards and referral platform operates. These technologies work as a single
            integrated system and cannot be selectively disabled without disabling the service
            entirely. A full cookie wall is in place &mdash; please refer to Section 4 for details
            on our consent model.
          </p>

          <p>We use the following categories of cookies and tracking technologies:</p>

          <ul>
            <li>
              (a)&nbsp;&nbsp;<b>Essential Platform Cookies:</b> Required for Buygoon.com to operate
              securely. They enable core functions including login, account authentication, session
              management, platform security, and Cashback attribution tracking. In addition to
              cookies, Buygoon&rsquo;s attribution system uses a pseudonymous member sub-ID appended
              to affiliate links at the moment of click-through, as described in Section 3.1.
            </li>
            <li>
              (b)&nbsp;&nbsp;<b>Platform Preferences:</b> Enable Buygoon.com to remember your
              settings and preferences including your preferred stores, saved merchants, preferred
              locale, and account preferences.
            </li>
            <li>
              (c)&nbsp;&nbsp;<b>Analytics and Performance:</b> Help us understand how visitors use
              Buygoon.com by measuring page performance, user behaviour patterns, and platform
              usage.
            </li>
            <li>
              (d)&nbsp;&nbsp;<b>Merchant Advertising:</b> Enable us to display merchant
              advertising and Cashback offers on the Buygoon platform. The same merchant
              advertising and offers are shown to all members and are not personalized or targeted
              to any individual member's interests, profile, or shopping history.
            </li>
          </ul>

          <p>
            Information about the cookies and tracking technologies in use on Buygoon.com is
            available on our <a href="/policies/cookie-policy.html">Cookie Policy page</a>.
          </p>

          <h2>9. Data Storage, Retention, and Protection</h2>

          <h4>9.1 Storage Locations</h4>

          <p>
            Personal information is stored in secure servers located in the United States and
            Ireland. Our primary hosting infrastructure is provided by Heroku (Salesforce, Inc.) and
            Amazon Web Services. For services that require international data transfer, we take
            steps to conduct such transfers in compliance with applicable Canadian privacy laws
            including PIPEDA.
          </p>

          <h4>9.2 Data Retention</h4>

          <p>
            We retain your personal information for as long as your Buygoon account remains active.
            If you close your account or request deletion of your data, we will delete or anonymize
            your personal information within a reasonable period, subject to the following
            exceptions:
          </p>

          <ul>
            <li>
              (a)&nbsp;&nbsp;<b>Tax and financial records:</b> We retain transaction records,
              payment records, Cashback attribution data, and any associated personal information
              &mdash; including legal names and tax identification numbers &mdash; for a period of
              seven years following the end of the tax year in which the transaction occurred. This
              retention is required to comply with the Income Tax Act (Canada), Canada Revenue
              Agency (CRA) record-keeping requirements, and applicable United States Internal
              Revenue Service (IRS) record-keeping requirements.
            </li>
            <li>
              (b)&nbsp;&nbsp;<b>Consent records:</b> We retain consent records as required by
              applicable privacy laws to demonstrate compliance with our consent obligations.
            </li>
            <li>
              (c)&nbsp;&nbsp;<b>Legal claims:</b> We may retain information where necessary to
              establish, exercise, or defend legal claims.
            </li>
            <li>
              (d)&nbsp;&nbsp;<b>Regulatory requirements:</b> We may retain information where
              required to do so by applicable law, court order, or regulatory authority.
            </li>
            <li>
              (e)&nbsp;&nbsp;<b>Financial records retained at Buygoon&rsquo;s discretion:</b>{' '}
              Buygoon retains the right, at its sole and absolute discretion, to retain financial
              records and associated personal information for such period as Buygoon determines
              necessary or appropriate for legitimate business purposes, including without
              limitation legal defence, fraud investigation, audit, regulatory compliance, platform
              integrity, and business continuity. This retention right survives account cancellation
              and cannot be overridden by any Member deletion request.
            </li>
          </ul>

          <p>
            Where a member requests deletion of their personal information, Buygoon retains the
            right to retain financial records and associated personal information pursuant to its
            legal obligations and legitimate business purposes as described above. Deletion requests
            cannot override these retention rights. We will notify the requesting member of any such
            retention and the basis for it.
          </p>

          <p>
            <b>De-identified and Aggregated Data:</b> Buygoon may anonymize, aggregate, or
            de-identify personal information, at which point such data is no longer personal
            information under applicable law. Buygoon retains the right to use, share, and retain
            such de-identified or aggregated data indefinitely for any lawful business purpose,
            including analytics, product development, research, and service improvement, including
            after a member&rsquo;s account is deleted or deactivated, provided the data cannot
            identify any individual.
          </p>

          <h4>9.3 Data Protection Measures</h4>

          <ul>
            <li>
              (a)&nbsp;&nbsp;<b>Encryption:</b> All data in transit is protected using HTTPS/TLS
              encryption. Data at rest is encrypted using industry-standard encryption technologies.
            </li>
            <li>
              (b)&nbsp;&nbsp;<b>Access control:</b> Access to personal information is strictly
              limited to authorized personnel with a legitimate business need. We enforce strict
              access controls and regularly review permissions.
            </li>
            <li>
              (c)&nbsp;&nbsp;<b>Security monitoring:</b> We employ security monitoring systems to
              detect and respond to potential security incidents promptly.
            </li>
            <li>
              (d)&nbsp;&nbsp;<b>Consent management:</b> All tracking technologies are gated behind
              explicit consent via our Cookiebot cookie wall.
            </li>
            <li>
              (e)&nbsp;&nbsp;<b>Vendor management:</b> Third-party service providers are selected
              based on their adherence to appropriate data protection standards, providing a level
              of protection comparable to that required under applicable Canadian privacy law.
            </li>
          </ul>

          <p>
            While Buygoon implements the measures described above, no system is 100% secure. Members
            should exercise caution when sharing information online.
          </p>

          <h4>9.4 Data Integrity and Purpose Limitation</h4>

          <p>
            Buygoon limits the collection of personal information to what is necessary for the
            identified purposes set out in this Privacy Policy. Personal information will not be
            used or disclosed for purposes other than those for which it was collected, except with
            member consent or as required by law. Buygoon takes reasonable steps to ensure that
            personal information is accurate, complete, and current for the purposes for which it is
            used.
          </p>

          <h2>10. Tax Reporting Obligations and Government Disclosure</h2>

          <h4>10.1 Tax Reporting Obligations</h4>

          <p>
            Buygoon is required by law to collect, retain, and report financial information about
            members who receive Cashback and Buygoon Bonus Cash payments above applicable reporting
            thresholds under United States and Canadian tax law. Specifically:
          </p>

          <ul>
            <li>
              (a)&nbsp;&nbsp;<b>United States members:</b> Buygoon is required to file IRS Form 1099
              for any member who receives USD $600 or more in Cashback and Buygoon Bonus Cash
              payments in a single calendar year. To comply with this obligation, we are required to
              collect your legal name, mailing address, and Social Security Number (SSN) or
              Individual Taxpayer Identification Number (ITIN) before issuing payment once your
              earnings approach this threshold.
            </li>
            <li>
              (b)&nbsp;&nbsp;<b>Canadian members:</b> Buygoon is required to comply with Canada
              Revenue Agency (CRA) reporting requirements for payments to individuals. We may be
              required to collect your legal name, mailing address, and Social Insurance Number
              (SIN) or Business Number (BN) where applicable under CRA rules.
            </li>
          </ul>

          <p>
            This information is collected solely for the purpose of fulfilling our mandatory tax
            reporting obligations. It is retained for the seven-year period described in Section
            9.2 and is not used for any other purpose.
          </p>

          <h4>10.2 Tax Compliance Service Providers</h4>

          <p>
            To assist with IRS Form 1099 filing, CRA reporting, and secure collection of tax
            identification numbers, Buygoon may engage one or more specialized third-party tax
            compliance service providers. These providers are selected for their expertise in tax
            compliance technology and their adherence to applicable data security and privacy
            standards.
          </p>

          <p>
            When a specific tax compliance service provider is engaged, that provider will be added
            to the third-party services table in Section 7, including their name, the data they
            receive, and a link to their privacy policy. Tax identification numbers and related
            personal information shared with such providers will be used exclusively for the purpose
            of fulfilling Buygoon&rsquo;s tax reporting obligations and will be subject to
            appropriate contractual obligations requiring the provider to maintain appropriate
            security and confidentiality measures.
          </p>

          <p>
            Whether tax compliance is handled through a third-party service provider or through
            Buygoon&rsquo;s own technology, the categories of information collected, the purposes of
            collection, and the retention periods described in this section remain the same.
          </p>

          <h4>10.3 Member Acknowledgment of Tax Reporting</h4>

          <p>
            By using Buygoon and receiving Cashback or Buygoon Bonus Cash payments, you acknowledge
            and agree that:
          </p>

          <ul>
            <li>
              (a)&nbsp;&nbsp;Buygoon is required by law to report payments above applicable
              thresholds to the IRS, CRA, and any other applicable tax authority.
            </li>
            <li>
              (b)&nbsp;&nbsp;You are responsible for accurately reporting all income received
              through Buygoon on your personal or business tax returns in accordance with the laws
              of your jurisdiction.
            </li>
            <li>
              (c)&nbsp;&nbsp;Buygoon may request your legal name, mailing address, Social Security
              Number, Social Insurance Number, or other tax identification information as a
              condition of receiving payment once your earnings reach applicable reporting
              thresholds.
            </li>
            <li>
              (d)&nbsp;&nbsp;Refusal to provide required tax identification information may result
              in Buygoon being required to apply backup withholding to your payments in accordance
              with applicable law.
            </li>
          </ul>

          <h4>10.4 Sensitive Personal Information</h4>

          <p>
            To the extent required by applicable law, Buygoon treats the following categories of
            information as sensitive personal information: Social Security Numbers, Social Insurance
            Numbers, and Tax Identification Numbers collected solely for the purpose of fulfilling
            mandatory tax reporting obligations under applicable US and Canadian law. This sensitive
            personal information is used exclusively to provide the Cashback service and to fulfill
            Buygoon&rsquo;s legal tax reporting obligations. It is never used to infer
            characteristics about members, for advertising or marketing purposes, or for any purpose
            other than those for which it was collected.
          </p>

          <h4>10.5 Government Disclosure</h4>

          <p>
            Buygoon may disclose any personal information &mdash; including financial transaction
            records, payment amounts, member identity information, tax identification numbers, and
            any other information in our possession &mdash; to any government authority, tax
            authority, regulatory body, or law enforcement agency when:
          </p>

          <ul>
            <li>(a)&nbsp;&nbsp;Required to do so by applicable law, statute, or regulation.</li>
            <li>
              (b)&nbsp;&nbsp;Required to do so by a lawful court order, subpoena, legal process, or
              government demand.
            </li>
            <li>
              (c)&nbsp;&nbsp;Required to fulfill our tax reporting obligations to the IRS, CRA, or
              any other applicable tax authority.
            </li>
            <li>
              (d)&nbsp;&nbsp;Necessary to cooperate with a government investigation or regulatory
              inquiry.
            </li>
            <li>
              (e)&nbsp;&nbsp;Necessary to protect the rights, property, or safety of Buygoon, its
              members, or the public.
            </li>
          </ul>

          <p>
            Such disclosures will be made without prior notice to the affected member where notice
            is prohibited by law, where providing notice would compromise the purpose of the
            disclosure, or where providing notice is not reasonably practicable in the
            circumstances. Where we are permitted by law to notify a member of a government demand
            for their information, we will make reasonable efforts to do so.
          </p>

          <p>
            Members cannot restrict, limit, or prohibit Buygoon from making disclosures that are
            required by law or lawful government demand. No privacy rights asserted under this
            Privacy Policy or applicable privacy laws override Buygoon&rsquo;s legal obligations to
            disclose information to government authorities as required by law.
          </p>

          <h2>11. International Data Transfers</h2>

          <p>
            Our third-party service providers are located primarily in the United States. When your
            personal information is transferred to the United States or other jurisdictions, we
            ensure that such transfers are conducted in compliance with PIPEDA and other applicable
            Canadian privacy laws. We require our service providers to maintain data protection
            standards comparable to those required under applicable Canadian privacy law. Each
            third-party service provider listed in Section 7 maintains its own privacy policy,
            accessible on that provider&rsquo;s website.
          </p>

          <h2>12. Children&rsquo;s Privacy</h2>

          <p>
            Buygoon.com is not intended for, and does not knowingly collect personal information
            from, any person under the age of 18. Our platform involves financial transactions and
            rewards mechanisms that are appropriate for adults only. By registering for and using
            Buygoon, you represent and warrant that you are at least 18 years of age.
          </p>

          <p>
            If we become aware that we have inadvertently collected personal information from a
            person under the age of 18, we will take prompt steps to delete such information from
            our records. If you believe that we may have collected personal information from a
            minor, please contact us immediately at{' '}
            <a href="mailto:privacy@buygoon.com">privacy@buygoon.com</a>.
          </p>

          <h2>13. Direct Marketing and Communications</h2>

          <p>
            We may use your email address and in-app messaging to send you direct marketing
            communications about our products, services, merchant offers, promotions, and other
            relevant information. We will obtain your explicit consent before sending you direct
            marketing communications.
          </p>

          <p>
            Every direct marketing communication we send will include a clear and easy mechanism to
            unsubscribe or opt out of future marketing communications. You may opt out at any time
            by using the unsubscribe link in any marketing email or by contacting us at{' '}
            <a href="mailto:privacy@buygoon.com">privacy@buygoon.com</a>.
          </p>

          <p>
            Please note that opting out of marketing communications does not affect transactional
            communications related to your account &mdash; such as Cashback credit notifications,
            tax reporting requests, account security alerts, and policy updates.
          </p>

          <h2>14. Your Privacy Rights</h2>

          <p>
            We recognize and respect your rights regarding your personal information under
            applicable Canadian and United States privacy laws.
          </p>

          <ul>
            <li>
              (a)&nbsp;&nbsp;<b>Right of Access:</b> You have the right to request access to the
              personal information we hold about you.
            </li>
            <li>
              (b)&nbsp;&nbsp;<b>Right to Rectification:</b> You have the right to request correction
              of any personal information we hold about you that is inaccurate or incomplete.
            </li>
            <li>
              (c)&nbsp;&nbsp;<b>Right to Object:</b> You have the right to object to the processing
              of your personal information for direct marketing purposes at any time.
            </li>
            <li>
              (d)&nbsp;&nbsp;<b>Right to Withdraw Consent:</b> You may withdraw your consent to our
              use of cookies and tracking technologies at any time via the Privacy Settings icon on
              the site. Please note that withdrawal of consent will disable your access to the
              Buygoon platform and your ability to earn future Cashback and Buygoon Bonus Cash. It
              does not affect our right to retain information required by law.
            </li>
          </ul>

          <p>
            <b>How to Exercise Your Rights &mdash; Direct Communication Requirement:</b> Privacy
            rights requests must be submitted directly by the member to whom the personal
            information relates, using the email address associated with your Buygoon account.
            Buygoon is not obligated to respond to requests submitted through third-party agents,
            representatives, or intermediaries. Where a third party is legally authorized to act on
            a member&rsquo;s behalf by operation of law &mdash; such as an executor of an estate, a
            court-appointed guardian, or a person holding a valid power of attorney &mdash; Buygoon
            reserves the right to require satisfactory proof of that legal authority before
            responding to any such request.
          </p>

          <p>
            To exercise any of these rights, please contact us at{' '}
            <a href="mailto:privacy@buygoon.com">privacy@buygoon.com</a>. We will respond to your
            request within the timeframes required by applicable law.
          </p>

          <p>
            <b>Right of Appeal (Where Applicable):</b> To the extent required by applicable law, you
            may have the right to appeal Buygoon&rsquo;s decision regarding a privacy rights
            request. If such a right applies in your jurisdiction, you may submit an appeal by
            contacting <a href="mailto:privacy@buygoon.com">privacy@buygoon.com</a> within 60 days
            of receiving Buygoon&rsquo;s response. Buygoon will respond to any such appeal within
            the timeframe required by applicable law.
          </p>

          <p>
            <b>Non-Retaliation (Where Applicable):</b> To the extent required by applicable law,
            Buygoon will not deny you service, reduce your Cashback rates, or otherwise penalize you
            solely because you exercised a privacy right available to you under applicable law, such
            as requesting access to or correction of your personal information. For the avoidance of
            doubt, the following does not constitute retaliation and you expressly acknowledge and
            agree that it does not: denial of access to the Buygoon platform, including all Cashback
            and Buygoon Bonus Cash functionality, where that denial results from your choice not to
            consent to the tracking technologies that are a mandatory and non-severable technical
            condition of service delivery. Consent to those tracking technologies is not a privacy
            right &mdash; it is a prerequisite to using the service.
          </p>

          <h2>15. Data Breach Notification</h2>

          <p>
            In the event of a data breach that poses a risk to your personal information, we have
            established clear procedures for promptly identifying, assessing, and responding to the
            breach.
          </p>

          <ul>
            <li>
              (a)&nbsp;&nbsp;<b>Assessment:</b> Upon discovery of a potential breach, we will
              conduct a thorough assessment to determine the nature, scope, and potential impact of
              the breach.
            </li>
            <li>
              (b)&nbsp;&nbsp;<b>Regulatory notification:</b> Where required by applicable law, we
              will notify the relevant regulatory authorities as soon as feasible after discovering
              a reportable breach.
            </li>
            <li>
              (c)&nbsp;&nbsp;<b>Individual notification:</b> If a breach poses a significant risk to
              your personal information, we will notify you as soon as feasible via email and/or
              website notification.
            </li>
            <li>
              (d)&nbsp;&nbsp;If you have any questions or concerns about a potential data breach,
              please contact us at <a href="mailto:privacy@buygoon.com">privacy@buygoon.com</a>.
            </li>
          </ul>

          <h2>16. Policy Updates and Changes</h2>

          <p>
            We may update this Privacy Policy from time to time to reflect changes in legal
            requirements, industry standards, our business operations, or the technologies we use.
            We will indicate the effective date of any updated policy at the top of this document.
          </p>

          <p>
            To the extent required by applicable law, Buygoon will notify members of material
            changes to this Privacy Policy by sending notice to the email address members have on
            file with Buygoon. For material changes that require your consent under applicable law,
            we will seek your explicit opt-in consent before implementing the changes.
          </p>

          <p>
            <b>Member Email Currency Obligation:</b> Members are responsible for maintaining a
            current and accurate email address in their Buygoon account at all times and must
            notify Buygoon immediately upon any change to their email address. Notification sent to
            the email address on file constitutes valid notice regardless of whether it is received.
            This email currency obligation is also reflected in Buygoon&rsquo;s Terms and
            Conditions.
          </p>

          <h2>17. Dispute Resolution and Governing Law</h2>

          <p>
            This Privacy Policy is governed by and construed in accordance with the laws of the
            Province of Ontario and the applicable federal laws of Canada, without regard to
            conflict of law principles.
          </p>

          <p>
            Any dispute, claim, or controversy arising out of or relating to this Privacy Policy or
            the collection, use, or disclosure of your personal information shall be resolved
            exclusively through binding arbitration in Ontario, Canada, in accordance with the
            dispute resolution provisions set out in Buygoon&rsquo;s Terms and Conditions. By using
            Buygoon.com, you agree to resolve all privacy-related disputes individually and waive
            any right to participate in any class action, class arbitration, or representative
            proceeding. Please review the Terms and Conditions for complete details.
          </p>

          <h2>18. Contact Us</h2>

          <p>
            If you have any questions, concerns, or requests relating to this Privacy Policy or how
            we handle your personal information, please contact us at{' '}
            <a href="mailto:privacy@buygoon.com">privacy@buygoon.com</a>.
          </p>

          <p>
            All privacy inquiries, rights requests, and complaints must be directed to{' '}
            <a href="mailto:privacy@buygoon.com">privacy@buygoon.com</a>. This is the sole contact
            method for all privacy-related communications. If and when required by applicable law,
            Buygoon will make a physical mailing address available upon request.
          </p>

          <p>
            Members may contact <a href="mailto:privacy@buygoon.com">privacy@buygoon.com</a> to
            obtain information about Buygoon&rsquo;s privacy policies and practices, to the extent
            required by applicable law.
          </p>

          <p>
            We are committed to addressing your privacy inquiries promptly and transparently and
            will respond to all legitimate privacy requests within the timeframes required by
            applicable law.
          </p>
          </div>
        </Col>
      </Row>
    </Layout>
  )
}

export default PrivacyPolicy
